import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  Renderer2,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Params, Router } from '@angular/router';
import {
  CatalogsFacade,
  CultureFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import { UrlSafePipe } from '../../pipes';
import { combineLatest, filter, Observable, of } from 'rxjs';
import {
  BoelsBaseSite,
  BoelsCmsLinkComponent,
  BoelsTeaserComponentData,
} from '@boels/models';
import { map, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface RouteParts {
  /** Path in the Angular-like array format */
  path?: string[];

  /** Query params */
  queryParams?: Params;

  /** Hash fragment */
  fragment?: string | null;
}

@Directive({
  selector: 'a[boelsGenericLink]',
})
export class GenericLinkDirective implements OnInit, OnChanges {
  // Injects
  #router = inject(Router);
  #renderer = inject(Renderer2);
  #el = inject(ElementRef);
  #cultureFacade = inject(CultureFacade);
  #urlSafePipe = inject(UrlSafePipe);
  #catalogsFacade = inject(CatalogsFacade);
  #siteContextFacade = inject(SiteContextFacade);
  #destroyRef = inject(DestroyRef);

  /**
   * Parsed parts of the @Input `url`, when it's a local URL.
   * It should not be used when the `url` is external.
   * @see `url`
   */
  protected routeParts: RouteParts = {};

  /**
   * Used to split url into 2 parts:
   * 1. the path
   * 2. query params + hash fragment
   */
  protected readonly PATH = {
    /**
     * Used to split url into 2 parts:
     * 1. the path
     * 2. query params + hash fragment
     */
    SPLIT: /(^[^#?]*)(.*)/,
    /**
     * Pattern matching string starting with `http://` or `https://`.
     */
    HTTP_PROTOCOL_REGEX: /^https?:\/\//i,

    /**
     * Pattern matching string starting with `mailto:`.
     */
    MAILTO_PROTOCOL_REGEX: /^mailto:/i,

    /**
     * Pattern matching string starting with `tel:`.
     */
    TEL_PROTOCOL_REGEX: /^tel:/i,
  };

  // Inputs
  url = input<string | string[] | undefined>();
  isExternal = input<boolean | undefined>();
  componentData = input<BoelsTeaserComponentData | undefined>(undefined);
  link = input<BoelsCmsLinkComponent | undefined>();

  // Signals
  categoryCode = signal<string | undefined>(undefined);
  cmsRouterLink = signal<any[] | undefined>(undefined);
  productCode = signal<string | undefined>(undefined);

  ngOnInit() {
    const link = this.link() || this.componentData()?.link;
    if (link) {
      this.processLink(link);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['url']) {
      this.handleUrlChanges(changes['url'].currentValue);
    }
  }

  /**
   * Processes the link by handling either its category or URL.
   */
  processLink(link: BoelsCmsLinkComponent): void {
    if (link.category) {
      this.categoryCode.set(link.category);
      this.getCategoryLinkData(link.category)
        .pipe(take(1))
        .subscribe((url) => {
          this.handleUrlChanges(url);
        });
    } else if (link.url) {
      this.handleUrlChanges(link.url);
    }
  }

  /**
   * Handles changes to the provided URL.
   * If the URL is external, sets the appropriate attributes (e.g., `href`, `rel`).
   * If the URL is internal, adds a click listener, parses the URL into parts,
   * and generates a CMS router link.
   *
   * @param url - The URL to process, either as a string or an array of path segments.
   */
  private handleUrlChanges(url: string | string[]): void {
    if (this.isExternalUrl(url)) {
      this.setAttributes('href', url as string);
      this.setAttributes(
        'rel',
        this.getAttribute('target') === '_blank' ? '_noopener' : '',
      );
    } else {
      this.addClickListener();
      this.setUrlParts(url);
      this.getCmsRouterLink(this.routeParts.path);
    }
  }

  /**
   * Adds a click listener to the element.
   */
  private addClickListener(): void {
    this.#renderer.listen(
      this.#el.nativeElement,
      'click',
      this.handleClick.bind(this),
    );

    this.#renderer.listen(
      this.#el.nativeElement,
      'keyup',
      (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          this.handleClick();
          event.preventDefault();
        }
      },
    );
  }

  /**
   * Retrieves category link data based on the provided link category.
   * Combines the active culture and base site to fetch the category details,
   * then constructs a URL-safe breadcrumb path.
   *
   * @param linkCategory - The category identifier for the link.
   * @returns An observable emitting an array of URL-safe string parts.
   */
  private getCategoryLinkData(linkCategory: string): Observable<string[]> {
    return combineLatest([
      this.#cultureFacade.getActiveCulture(),
      this.#siteContextFacade.getActiveBaseSiteEntity(),
    ]).pipe(
      filter(([, baseSite]) => !!baseSite?.uid),
      switchMap(([culture, baseSite]: [string, BoelsBaseSite]) =>
        baseSite.uid
          ? this.#catalogsFacade.getCategoryForId(
              baseSite.uid,
              culture,
              linkCategory,
            )
          : of(undefined),
      ),
      filter(Boolean),
      map((cat) => {
        const urlParts = [];
        for (const breadCrumb of cat?.boelsBreadcrumbs || []) {
          urlParts.push(this.#urlSafePipe.transform(breadCrumb.categoryName));
        }
        urlParts.push(this.#urlSafePipe.transform(cat?.name));
        urlParts.push('c');
        urlParts.push(cat?.id);
        return urlParts.filter((part) => part !== undefined);
      }),
    );
  }

  /**
   * Determine if url is external
   */
  isExternalUrl(url: string | string[] | undefined): boolean {
    return (
      this.isExternal() ??
      (typeof url === 'string' &&
        (this.PATH.HTTP_PROTOCOL_REGEX.test(url) ||
          this.PATH.MAILTO_PROTOCOL_REGEX.test(url) ||
          this.PATH.TEL_PROTOCOL_REGEX.test(url)))
    );
  }

  /**
   * Parses the given url and sets the property `urlParts` accordingly.
   */
  protected setUrlParts(url: string | string[]) {
    if (typeof url === 'string') {
      this.routeParts = this.splitUrl(this.getAbsoluteUrl(url));
    } else {
      this.routeParts = { path: url };
    }
  }

  /**
   * Parses the given string into 3 parts:
   * - string path (wrapped in an array to be compatible with Angular syntax for the `routerLink`)
   * - query params (as an object)
   * - hash fragment (string)
   */
  protected splitUrl(url = ''): RouteParts {
    const { queryParams, fragment } = this.#router.parseUrl(url);

    const [, path] = url.match(this.PATH.SPLIT) ?? [undefined, ''];

    // wrap path in an array, to have the Angular-like path format
    return { path: [path ?? ''], queryParams, fragment };
  }

  /**
   * Prepends a leading slash to the given URL string, in case it doesn't have it.
   */
  protected getAbsoluteUrl(url: string): string {
    return url.startsWith('/') ? url : '/' + url;
  }

  /**
   * Generates and sets the CMS router link based on the provided commands.
   * The link is prefixed with the active culture and sanitized using the UrlSafePipe.
   *
   * @param commands - The navigation commands, either as an array or a string.
   */
  getCmsRouterLink(commands: any[] | string | undefined | null) {
    if (!commands) {
      this.cmsRouterLink.set(undefined);
      return;
    }

    this.#cultureFacade
      .getActiveCulture()
      .pipe(filter(Boolean), takeUntilDestroyed(this.#destroyRef))
      .subscribe((culture) => {
        this.cmsRouterLink.set(
          Array.isArray(commands)
            ? commands.reduce(
                (previousValue, currentValue) => [
                  ...previousValue,
                  ...currentValue
                    .split('/')
                    .filter((value: string) => value.length > 0)
                    .map((value: string) => this.#urlSafePipe.transform(value)),
                ],
                ['/' + culture],
              )
            : [
                '/' + culture,
                ...commands
                  .split('/')
                  .filter((value: string) => value.length > 0)
                  .map((value: string) => this.#urlSafePipe.transform(value)),
              ],
        );
      });
  }

  /**
   * Handles the click event for the directive.
   * If the URL is internal and a CMS router link is available, navigates to the link
   * with the associated query parameters and hash fragment.
   */
  handleClick() {
    if (this.cmsRouterLink() === undefined) return;

    if (this.getAttribute('target') === '_blank') {
      const queryParams = new URLSearchParams(
        this.routeParts.queryParams,
      ).toString();
      const fragment = this.routeParts.fragment
        ? `#${this.routeParts.fragment}`
        : '';
      const fullUrl = `${this.cmsRouterLink()?.join('/') ?? ''}${queryParams ? '?' + queryParams : ''}${fragment}`;
      window.open(fullUrl, '_blank');
    } else if (!this.isExternalUrl(this.url())) {
      this.#router.navigate(this.cmsRouterLink() as any[], {
        queryParams: this.routeParts.queryParams,
        fragment: this.routeParts.fragment ?? undefined,
      });
    }
  }

  setAttributes(attr: string, value: string | undefined) {
    if (value) {
      this.#renderer.setAttribute(this.#el.nativeElement, attr, value);
    }
  }

  getAttribute(attr: string): string | null {
    return this.#el.nativeElement.getAttribute(attr);
  }
}
